import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export interface LogoImageProps {
  src?: string | null;
  alt?: string;
  size?: number;
  fallbackText?: string;
  paddingPx?: number;
  sx?: SxProps<Theme>;
}

export const LogoImage: React.FC<LogoImageProps> = ({
  src,
  alt,
  size = 32,
  fallbackText,
  paddingPx,
  sx,
}) => {
  const fallback = (fallbackText || '').trim();
  const text = fallback ? fallback.slice(0, 2).toUpperCase() : '?';
  const resolvedPadding = Math.max(
    0,
    paddingPx ?? (size >= 64 ? 6 : size >= 48 ? 4 : size >= 32 ? 3 : size >= 24 ? 2 : 1)
  );
  const fallbackFontSize = size >= 48 ? 18 : size >= 32 ? 14 : size >= 24 ? 12 : 11;

  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)'),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.24)' : 'rgba(0,0,0,0.08)'),
        flexShrink: 0,
        ...sx,
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: `${resolvedPadding}px`,
            display: 'block',
            boxSizing: 'border-box',
          }}
        />
      ) : (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            fontSize: fallbackFontSize,
            color: 'text.secondary',
          }}
        >
          {text}
        </Typography>
      )}
    </Box>
  );
};
